/* eslint-disable */
import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Clock, Loader2, Check, FileDown, Upload, FileText, PenLine } from "lucide-react";
import { queueTimesheet, newClientUuid } from "@/lib/offlineQueue";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import OfflineBadge from "@/components/OfflineBadge";
import FeuillesMensuelles from "@/components/FeuillesMensuelles";

const TYPES = {
  travail: { label: "Travail", cls: "bg-slate-100 text-slate-700" },
  teletravail: { label: "Télétravail", cls: "bg-emerald-100 text-emerald-700" },
  conge: { label: "Congé payé", cls: "bg-blue-100 text-blue-700" },
  rtt: { label: "RTT", cls: "bg-blue-100 text-blue-700" },
  maladie: { label: "Maladie", cls: "bg-red-100 text-red-700" },
  accident: { label: "Accident du travail", cls: "bg-red-100 text-red-700" },
  ferie: { label: "Férié", cls: "bg-amber-100 text-amber-700" },
  weekend: { label: "Week-end", cls: "bg-slate-100 text-slate-500" },
  chome: { label: "Chômé", cls: "bg-slate-100 text-slate-500" },
  sortie_autorisee: { label: "Sortie autorisée", cls: "bg-amber-100 text-amber-700" },
};

const EMPTY = {
  mission_id: "", salarie_id: "", date: new Date().toISOString().slice(0,10), type_journee: "travail",
  heure_arrivee: "08:00", heure_pause_debut: "12:00", heure_pause_fin: "13:00", heure_depart: "17:00",
  teletravail: false, lieu: "", commentaire: "",
};

const calcEffectives = (f) => {
  const toMin = (t) => { if (!t) return null; const [h,m] = t.split(":").map(Number); return h*60+m; };
  const a = toMin(f.heure_arrivee), pd = toMin(f.heure_pause_debut), pf = toMin(f.heure_pause_fin), d = toMin(f.heure_depart);
  if (a == null || d == null) return { heures: null, pause: 0 };
  const pause = (pd != null && pf != null && pf >= pd) ? pf - pd : 0;
  const heures = Math.max(0, (d - a) - pause) / 60;
  return { heures: Math.round(heures*100)/100, pause };
};

export default function Timesheets() {
  const [items, setItems] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filterMois, setFilterMois] = useState(new Date().toISOString().slice(0,7));
  const [pdfSalarie, setPdfSalarie] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    const [a, b, c] = await Promise.all([
      api.get("/timesheets", { params: { mois: filterMois } }),
      api.get("/salaries"),
      api.get("/missions"),
    ]);
    setItems(a.data); setSalaries(b.data); setMissions(c.data); setLoading(false);
  };
  useEffect(() => { load(); }, [filterMois]);

  // Hook offline : réessaye auto au retour de connexion et rafraîchit la liste
  useOfflineSync({ onSynced: () => load() });

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.mission_id) delete payload.mission_id;
    // Détection automatique de la vacation passant minuit (heure de départ < arrivée)
    if (payload.heure_arrivee && payload.heure_depart && payload.heure_depart < payload.heure_arrivee) {
      payload.crosses_midnight = true;
    }
    // Idempotence : générer un client_uuid stable AVANT toute tentative réseau,
    // réutilisé en retry/offline. Si le serveur reçoit deux fois le même, il renvoie le résultat déjà enregistré.
    if (!payload.client_uuid) payload.client_uuid = newClientUuid();
    if (!navigator.onLine) {
      try {
        await queueTimesheet(payload);
        toast.success("Hors-ligne : feuille enregistrée localement, synchronisation au retour du réseau.");
        setOpen(false); setForm(EMPTY);
      } catch (err) { toast.error("Impossible d'enregistrer en local"); }
      return;
    }
    try {
      await api.post("/timesheets", payload);
      toast.success("Feuille saisie"); setOpen(false); setForm(EMPTY); load();
    } catch (err) {
      // 409 : conflit d'idempotence (même client_uuid, contenu différent) → PAS d'écrasement
      if (err.response?.status === 409 && err.response?.data?.detail?.code === "timesheet_idempotency_conflict") {
        toast.error(
          "Modification non enregistrée : cette saisie a déjà été transmise sous une autre forme. " +
          "Merci de passer par le circuit de demande de correction.",
          { duration: 7000, id: "idempotency-conflict" }
        );
        return;
      }
      // Si l'erreur est réseau (pas de réponse), on tombe en offline-mode avec le même client_uuid
      if (!err.response) {
        try {
          await queueTimesheet(payload);
          toast.success("Réseau instable : feuille enregistrée localement, synchro automatique à venir.");
          setOpen(false); setForm(EMPTY);
          return;
        } catch {}
      }
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const validate = async (id) => {
    try { await api.post(`/timesheets/${id}/validate`); toast.success("Validée"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadFeuille = async () => {
    if (!pdfSalarie) { toast.error("Sélectionnez un salarié"); return; }
    const [annee, mois] = filterMois.split("-").map(Number);
    try {
      const resp = await api.get(`/timesheets/feuille-mensuelle/${pdfSalarie}/${annee}/${mois}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
      setTimeout(()=>URL.revokeObjectURL(url), 30000);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const exportCSV = async () => {
    try {
      const resp = await api.get("/timesheets/export.csv", { params: { mois: filterMois }, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `timesheets_${filterMois}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export généré");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const importCSV = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Sélectionnez un fichier CSV"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/timesheets/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} ligne(s) importée(s) · ${data.total_errors} erreur(s)`);
      setImportOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [signOpen, setSignOpen] = useState(false);
  const [signForm, setSignForm] = useState({ nom: "", qualite: "", role: "salarie" });
  const [signatures, setSignatures] = useState([]);

  const loadSignatures = async () => {
    if (!pdfSalarie) { setSignatures([]); return; }
    const [annee, mois] = filterMois.split("-").map(Number);
    try {
      const { data } = await api.get(`/timesheets/feuille-mensuelle/${pdfSalarie}/${annee}/${mois}/signatures`);
      setSignatures(data);
    } catch { setSignatures([]); }
  };
  useEffect(() => { loadSignatures(); }, [pdfSalarie, filterMois]);

  const [autoFacture, setAutoFacture] = useState(null);

  const signer = async () => {
    if (!pdfSalarie) { toast.error("Sélectionnez un salarié"); return; }
    if (!signForm.nom.trim()) { toast.error("Nom requis"); return; }
    const [annee, mois] = filterMois.split("-").map(Number);
    try {
      const { data } = await api.post(`/timesheets/feuille-mensuelle/${pdfSalarie}/${annee}/${mois}/signer`, signForm);
      if (data.auto_facture) {
        setAutoFacture(data.auto_facture);
        toast.success(`Feuille signée — Facture finale auto-générée : ${Number(data.auto_facture.montant).toLocaleString("fr-FR")} €`);
      } else {
        toast.success("Feuille signée électroniquement");
      }
      setSignOpen(false); setSignForm({ nom: "", qualite: "", role: "salarie" });
      loadSignatures();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const roleLabel = { salarie: "Salarié", employeur: "Entreprise", ge: "O'SCOP GE" };
  const remainingRoles = ["salarie", "employeur", "ge"].filter(r => !signatures.find(s => s.role === r));

  const salLabel = (id) => { const s = salaries.find(x => x.id === id); return s ? `${s.prenom} ${s.nom}` : "—"; };
  const totHeures = items.filter(t => t.type_journee === "travail" || t.type_journee === "teletravail").reduce((s,t) => s + Number(t.heures||0), 0);
  const preview = calcEffectives(form);

  return (
    <div data-testid="timesheets-page">
      <PageHeader
        title="Feuilles de temps"
        subtitle={`Saisie horaires détaillée avec pause méridienne, télétravail, congés et fériés. Total ${filterMois} : ${totHeures.toFixed(2)} h.`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <OfflineBadge />
            <Input type="month" value={filterMois} onChange={(e)=>setFilterMois(e.target.value)} data-testid="ts-month" className="h-9 w-auto" />
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="ts-export-csv"><FileDown className="h-3.5 w-3.5 mr-1" />Export CSV</Button>
            <Button variant="outline" size="sm" onClick={()=>setImportOpen(true)} data-testid="ts-import-btn"><Upload className="h-3.5 w-3.5 mr-1" />Import CSV</Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="ts-add-btn" size="sm" className="bg-[#C5A059] hover:bg-[#A88846] text-white"><Plus className="h-4 w-4 mr-1" />Saisie</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Saisie de feuille de temps</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Salarié *</Label>
                    <Select value={form.salarie_id} onValueChange={(v)=>setForm({...form, salarie_id: v})}>
                      <SelectTrigger data-testid="ts-salarie"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>{salaries.map(s=><SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Mission</Label>
                    <Select value={form.mission_id} onValueChange={(v)=>setForm({...form, mission_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Optionnel" /></SelectTrigger>
                      <SelectContent>{missions.map(m=><SelectItem key={m.id} value={m.id}>{m.libelle}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Date *</Label><Input type="date" required value={form.date} onChange={(e)=>setForm({...form, date: e.target.value})} /></div>
                  <div className="space-y-2">
                    <Label>Type de journée</Label>
                    <Select value={form.type_journee} onValueChange={(v)=>setForm({...form, type_journee: v})}>
                      <SelectTrigger data-testid="ts-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(TYPES).map(([k,v])=><SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {(form.type_journee === "travail" || form.type_journee === "teletravail") && (
                    <>
                      <div className="space-y-2 col-span-full grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div><Label className="text-xs">Arrivée</Label><Input type="time" value={form.heure_arrivee} onChange={(e)=>setForm({...form, heure_arrivee: e.target.value})} data-testid="ts-arrivee" /></div>
                        <div><Label className="text-xs">Pause début</Label><Input type="time" value={form.heure_pause_debut} onChange={(e)=>setForm({...form, heure_pause_debut: e.target.value})} data-testid="ts-pause-debut" /></div>
                        <div><Label className="text-xs">Pause fin</Label><Input type="time" value={form.heure_pause_fin} onChange={(e)=>setForm({...form, heure_pause_fin: e.target.value})} /></div>
                        <div><Label className="text-xs">Départ</Label><Input type="time" value={form.heure_depart} onChange={(e)=>setForm({...form, heure_depart: e.target.value})} data-testid="ts-depart" /></div>
                      </div>
                      <div className="col-span-full rounded-lg bg-[#FDF9F1] border border-[#C5A059]/30 p-3 text-sm" data-testid="ts-preview">
                        <strong>Calcul auto :</strong> Heures effectives = <span className="font-bold text-[#A88846]">{preview.heures?.toFixed(2) || "—"} h</span> · Pause méridienne <span className="font-bold">{preview.pause} min</span>
                      </div>
                      <div className="flex items-center gap-2 col-span-full">
                        <Checkbox id="tt" checked={form.teletravail} onCheckedChange={(v)=>setForm({...form, teletravail: !!v, type_journee: v ? "teletravail" : (form.type_journee === "teletravail" ? "travail" : form.type_journee)})} data-testid="ts-teletravail" />
                        <Label htmlFor="tt" className="cursor-pointer">Effectué en télétravail</Label>
                      </div>
                    </>
                  )}
                  <div className="space-y-2 col-span-full"><Label>Lieu</Label><Input value={form.lieu} onChange={(e)=>setForm({...form, lieu: e.target.value})} placeholder="Site de l'entreprise utilisatrice, domicile, etc." /></div>
                  <div className="space-y-2 col-span-full"><Label>Commentaire</Label><Textarea rows={2} value={form.commentaire} onChange={(e)=>setForm({...form, commentaire: e.target.value})} /></div>
                  <DialogFooter className="col-span-full"><Button type="submit" data-testid="ts-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Enregistrer</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {/* Feuille mensuelle PDF */}
      <div className="mb-5 bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-3 items-end" data-testid="ts-feuille-section">
        <div>
          <Label className="text-xs">Salarié pour feuille mensuelle</Label>
          <Select value={pdfSalarie} onValueChange={setPdfSalarie}>
            <SelectTrigger className="h-9 w-64 mt-1" data-testid="ts-feuille-salarie"><SelectValue placeholder="Sélectionner un salarié" /></SelectTrigger>
            <SelectContent>{salaries.map(s=><SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={downloadFeuille} data-testid="ts-feuille-pdf-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white h-9">
          <FileText className="h-4 w-4 mr-2" />Feuille de temps PDF — {filterMois}
        </Button>
        <Button variant="outline" onClick={()=>setSignOpen(true)} data-testid="ts-sign-btn" disabled={!pdfSalarie || remainingRoles.length === 0} className="border-[#C5A059] text-[#C5A059] hover:bg-[#FDF9F1] h-9">
          <PenLine className="h-4 w-4 mr-2" />Signer ({signatures.length}/3)
        </Button>
        <div className="text-xs text-slate-500 ml-auto">Le PDF mensuel inclut tous les jours, les week-ends, fériés, pauses méridiennes, totaux et un bloc signatures.</div>
      </div>

      {pdfSalarie && signatures.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-2" data-testid="ts-sig-badges">
          {signatures.map(sig => (
            <div key={sig.id} className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1 flex items-center gap-1.5">
              <Check className="h-3 w-3" /><strong>{roleLabel[sig.role]} :</strong> {sig.nom} · {sig.signed_at?.slice(0,10)}
            </div>
          ))}
        </div>
      )}

      <div className="mb-5">
        <FeuillesMensuelles defaultMois={filterMois} />
      </div>

      {autoFacture && (
        <div className="mb-5 rounded-xl border-2 border-emerald-400 bg-gradient-to-r from-emerald-50 to-[#FDF9F1] p-5 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between" data-testid="ts-auto-facture-banner">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0"><Check className="h-5 w-5" /></div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-700">Cycle clôturé · Facture finale auto-générée</div>
              <div className="text-lg font-semibold text-slate-900 mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                Appel final au coût réel : <span className="text-[#A88846]">{Number(autoFacture.montant).toLocaleString("fr-FR")} €</span>
              </div>
              <div className="text-xs text-slate-600 mt-1">Période {autoFacture.periode} · {autoFacture.heures_validees}h validées · Facture émise et notifiée aux gestionnaires.</div>
            </div>
          </div>
          <a href="/app/appels" className="inline-flex items-center gap-2 px-5 h-10 bg-[#C5A059] hover:bg-[#A88846] text-white rounded-md font-medium transition-colors text-sm">
            Voir la facture →
          </a>
        </div>
      )}

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Signature électronique de la feuille — {filterMois}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-[#FDF9F1] border border-[#C5A059]/30 p-3 text-sm text-slate-700">
              Signature simple (nom + IP + horodatage + hash SHA-256). Une signature par rôle (Salarié, Entreprise, O&apos;SCOP GE).
            </div>
            <div className="space-y-2">
              <Label>Rôle *</Label>
              <Select value={signForm.role} onValueChange={(v)=>setSignForm({...signForm, role: v})}>
                <SelectTrigger data-testid="ts-sign-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {remainingRoles.map(r => <SelectItem key={r} value={r}>{roleLabel[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Nom complet *</Label><Input value={signForm.nom} onChange={(e)=>setSignForm({...signForm, nom: e.target.value})} data-testid="ts-sign-nom" /></div>
            <div className="space-y-2"><Label>Qualité</Label><Input value={signForm.qualite} onChange={(e)=>setSignForm({...signForm, qualite: e.target.value})} placeholder="Ex. Représentant légal, Salarié, Gestionnaire" /></div>
            <DialogFooter><Button onClick={signer} data-testid="ts-sign-confirm" className="bg-[#C5A059] hover:bg-[#A88846] text-white"><PenLine className="h-4 w-4 mr-2" />Signer maintenant</Button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Import de feuilles de temps (CSV)</DialogTitle></DialogHeader>
          <form onSubmit={importCSV} className="space-y-4">
            <div className="rounded-lg bg-[#FDF9F1] border border-[#C5A059]/30 p-3 text-xs text-slate-700">
              Colonnes attendues (séparateur <code>;</code> ou <code>,</code>) :<br/>
              <code className="text-[11px]">date; salarie_id; mission_id; type_journee; heure_arrivee; heure_pause_debut; heure_pause_fin; heure_depart; pause_minutes; heures; teletravail; lieu; commentaire</code>
            </div>
            <div><Label>Fichier CSV</Label><Input ref={fileRef} type="file" accept=".csv" required data-testid="ts-import-file" /></div>
            <DialogFooter><Button type="submit" data-testid="ts-import-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Importer</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <Clock className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">Aucune saisie ce mois-ci.</p>
        </div>
      ) : (
        <>
          {/* Vue mobile : cartes compactes */}
          <div className="md:hidden space-y-3" data-testid="ts-cards-mobile">
            {items.map(t => (
              <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 text-sm">{t.date}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{salLabel(t.salarie_id)}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded shrink-0 ${TYPES[t.type_journee]?.cls || "bg-slate-100 text-slate-700"}`}>{TYPES[t.type_journee]?.label || t.type_journee}</span>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-50 rounded p-1.5">
                    <div className="text-[9px] uppercase text-slate-400">Arrivée</div>
                    <div className="text-xs font-mono font-medium">{t.heure_arrivee || "—"}</div>
                  </div>
                  <div className="bg-slate-50 rounded p-1.5">
                    <div className="text-[9px] uppercase text-slate-400">Départ</div>
                    <div className="text-xs font-mono font-medium">{t.heure_depart || "—"}</div>
                  </div>
                  <div className="bg-[#FDF9F1] border border-[#C5A059]/30 rounded p-1.5">
                    <div className="text-[9px] uppercase text-[#A88846]">Heures</div>
                    <div className="text-xs font-bold text-[#A88846]">{Number(t.heures || 0).toFixed(2)}h</div>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className={`text-[10px] px-2 py-0.5 rounded ${t.statut === "validee" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{t.statut}</span>
                  {t.teletravail && <span className="text-[10px] text-emerald-600">🏠 Télétravail</span>}
                  {t.statut !== "validee" && (
                    <Button size="sm" variant="outline" onClick={()=>validate(t.id)} className="h-7 text-xs" data-testid={`ts-validate-mobile-${t.id}`}>
                      <Check className="h-3 w-3 mr-1" />Valider
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Vue desktop : tableau */}
          <div className="hidden md:block bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-3">Date</th><th className="px-3 py-3">Salarié</th><th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Arrivée</th><th className="px-3 py-3">Pause</th><th className="px-3 py-3">Départ</th>
                <th className="px-3 py-3 text-right">Heures</th><th className="px-3 py-3">TT</th><th className="px-3 py-3">Lieu</th>
                <th className="px-3 py-3">Statut</th><th className="px-3 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map(t => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-3 text-slate-700">{t.date}</td>
                  <td className="px-3 py-3 text-slate-700">{salLabel(t.salarie_id)}</td>
                  <td className="px-3 py-3"><span className={`text-[10px] px-2 py-0.5 rounded ${TYPES[t.type_journee]?.cls || "bg-slate-100 text-slate-700"}`}>{TYPES[t.type_journee]?.label || t.type_journee}</span></td>
                  <td className="px-3 py-3 text-slate-600 font-mono text-xs">{t.heure_arrivee || "—"}</td>
                  <td className="px-3 py-3 text-slate-600 font-mono text-xs">{t.heure_pause_debut && t.heure_pause_fin ? `${t.heure_pause_debut}-${t.heure_pause_fin}` : (t.pause_minutes ? `${t.pause_minutes} min` : "—")}</td>
                  <td className="px-3 py-3 text-slate-600 font-mono text-xs">{t.heure_depart || "—"}</td>
                  <td className="px-3 py-3 text-right font-semibold">{Number(t.heures || 0).toFixed(2)}h</td>
                  <td className="px-3 py-3 text-center">{t.teletravail ? "✓" : ""}</td>
                  <td className="px-3 py-3 text-slate-600 text-xs truncate max-w-[120px]">{t.lieu || "—"}</td>
                  <td className="px-3 py-3"><span className={`text-xs px-2 py-1 rounded ${t.statut === "validee" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{t.statut}</span></td>
                  <td className="px-3 py-3 text-right">
                    {t.statut !== "validee" && <Button size="sm" variant="outline" onClick={()=>validate(t.id)} data-testid={`ts-validate-${t.id}`}><Check className="h-3 w-3 mr-1" />Valider</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
}
